"use client"

import { ChevronLeftIcon } from "@heroicons/react/20/solid"
import Button from "@src/components/Button"
import { LockSolidIcon } from "@src/icons"
import { useConfidentialMode } from "@src/providers/ConfidentialModeProvider"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { type ReactNode, useEffect } from "react"

const supportedRoutes = new Set([
  "/home",
  "/contacts",
  "/send",
  "/send/confidential",
  "/receive",
  "/history",
  "/near-x",
  "/transfer",
  "/referrals",
  "/settings",
  "/swap",
  "/crypto",
])

const getRouteDescription = (pathname: string) => {
  const segment = `/${pathname.split("/")[1] ?? ""}`

  switch (segment) {
    case "/deals":
      return "Private deals are not yet available in Confidential mode. Support for Deals and other features is coming soon."
    case "/history":
      return "History is not yet available in Confidential mode. Support for History and other features is coming soon."
    case "/send":
      return "For your privacy, move the funds from your Confidential account to your Main account first, then send from there."
    case "/perps":
      return "Perps are only available in Confidential mode."
    default:
      return "Confidential mode is not yet available on this feature. Support for more features is coming soon."
  }
}

const ConfidentialModeGuard = ({ children }: { children: ReactNode }) => {
  const { confidentialMode, setConfidentialMode } = useConfidentialMode()
  const pathname = usePathname()
  const router = useRouter()
  const searchParams = useSearchParams()
  const normalizedPath =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname
  const isPerpsRoute =
    normalizedPath === "/perps" || normalizedPath.startsWith("/perps/")
  const query = searchParams.toString()
  const redirectHref = (() => {
    if (confidentialMode && normalizedPath === "/send") {
      return query ? `/send/confidential?${query}` : "/send/confidential"
    }
    if (!confidentialMode && normalizedPath === "/send/confidential") {
      return query ? `/send?${query}` : "/send"
    }
    return null
  })()

  useEffect(() => {
    if (redirectHref == null) return
    router.replace(redirectHref)
  }, [redirectHref, router])

  if (redirectHref != null) return null

  if (!confidentialMode && (!isPerpsRoute || normalizedPath === "/perps"))
    return children
  if (
    supportedRoutes.has(normalizedPath) ||
    normalizedPath.startsWith("/earn") ||
    (confidentialMode && isPerpsRoute)
  )
    return children

  const description = getRouteDescription(normalizedPath)
  const isMainModePerps = !confidentialMode && isPerpsRoute

  return (
    <>
      {isMainModePerps && (
        <div className="dark fixed inset-x-0 top-0 z-50 px-4 pt-safe-offset-3 sm:px-6 sm:pt-safe-offset-5 xl:hidden">
          <div className="mx-auto w-full max-w-7xl sm:max-w-3xl">
            <Button
              size="lg"
              variant="secondary"
              className="size-10!"
              href="/home"
              aria-label="Back"
            >
              <ChevronLeftIcon className="size-5" />
            </Button>
          </div>
        </div>
      )}

      <div className="relative z-50 mx-auto mt-safe-offset-20 mb-2 w-full max-w-116 xl:mt-0 xl:mb-7 xl:max-w-4xl">
        <div className="-outline-offset-1 flex items-center gap-3 rounded-xl bg-gray-900 p-3 outline-1 outline-white/5 sm:gap-4 sm:rounded-2xl sm:p-4">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-green-500/15 sm:size-9">
            <LockSolidIcon className="size-4 text-green-500 sm:size-4.5" />
          </div>
          <span className="sr-only">
            Confidential mode is {confidentialMode ? "on" : "off"}
          </span>
          <div className="flex-1 text-pretty font-semibold text-sm/5 text-white tracking-tight sm:text-base/6">
            {description}
          </div>
          <Button
            onClick={() => setConfidentialMode(!confidentialMode)}
            variant="primary"
            size="sm"
            className="dark sm:h-10 sm:rounded-xl sm:px-4 sm:font-bold xl:h-9 xl:px-3"
            aria-label={`Turn ${confidentialMode ? "off" : "on"} Confidential mode`}
          >
            <span aria-hidden="true" className="sm:hidden">
              Turn {confidentialMode ? "off" : "on"}
            </span>
            <span aria-hidden="true" className="hidden sm:inline">
              Turn {confidentialMode ? "off" : "on"} Confidential mode
            </span>
          </Button>
        </div>
      </div>

      <div className="relative select-none" aria-hidden={true} inert={true}>
        {children}
        <div className="-inset-x-2 absolute top-8 bottom-0 z-10 bg-gray-25/50 xl:top-0 dark:bg-gray-850/60" />
      </div>
    </>
  )
}

export default ConfidentialModeGuard
